import { createSlice, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import bookingService from "./bookingService";
import { toast } from 'react-toastify';

export const getBookings = createAsyncThunk(
  "booking/get-bookings",
  async (thunkAPI) => {
    try {
      return await bookingService.getBookings();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteABooking = createAsyncThunk(
  "booking/delete-booking",
  async (id, thunkAPI) => {
    try {
      return await bookingService.deleteBooking(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getABooking = createAsyncThunk(
  "booking/get-booking",
  async (id, thunkAPI) => {
    try {
      return await bookingService.getBooking(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateABooking = createAsyncThunk(
  "booking/update-booking",
  async (booking, thunkAPI) => {
    try {
      return await bookingService.updateBooking(booking);
    } catch (error) {
      // Extract serializable parts of the Axios error
      const serializableError = {
        message: error.message,
        code: error.code,
        response: error.response ? error.response.data : null,
      };
      return thunkAPI.rejectWithValue(serializableError);
    }
  }
);


export const getUnreadBookings = createAsyncThunk(
  "booking/get-unread-bookings",
  async (_, thunkAPI) => {
    try {
      return await bookingService.getUnreadBookings();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const markBookingAsRead = createAsyncThunk(
  "booking/mark-as-read",
  async (id, thunkAPI) => {
    try {
      return await bookingService.markBookingAsRead(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const submitFeedback = createAsyncThunk(
  "booking/submit-feedback",
  async ({ id, email, userName, feedback, staffMember }, thunkAPI) => {
    try {
      return await bookingService.submitFeedback({ id, email, userName,  feedback, staffMember });
    } catch (error) {
      const serializableError = {
        message: error.message,
        code: error.code,
        response: error.response ? error.response.data : null,
      };
      return thunkAPI.rejectWithValue(serializableError);
    }
  }
);

export const resetState = createAction("Reset_all");

const initialState = {
  bookings: [],
  unreadBookings: [],
  isError: false,
  isLoading: false,
  isSuccess: false,
  message: "",
};

export const bookingSlice = createSlice({
  name: "bookings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getBookings.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getBookings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.bookings = action.payload;
      })
      .addCase(getBookings.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
      })
      .addCase(deleteABooking.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteABooking.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.deletedBooking = action.payload;
      })
      .addCase(deleteABooking.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
      })
      .addCase(getABooking.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getABooking.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.bookingName = action.payload.name;
        state.bookingMobile = action.payload.mobile;
        state.bookingEmail = action.payload.email;
   
        state.bookingStatus = action.payload.status;
      })
      .addCase(getABooking.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
      })
      .addCase(updateABooking.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateABooking.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.updatedBooking = action.payload;
      })
      .addCase(updateABooking.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
      })
      .addCase(getUnreadBookings.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUnreadBookings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.unreadBookings = action.payload.bookings;
      })
      .addCase(getUnreadBookings.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.error.message || "Failed to fetch unread bookings";
      })
      .addCase(markBookingAsRead.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(markBookingAsRead.fulfilled, (state) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.message = "Booking marked as read successfully";
      })
      .addCase(markBookingAsRead.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.error.message || "Failed to mark booking as read";
      })
      .addCase(submitFeedback.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(submitFeedback.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.feedbackResponse = action.payload;
        toast.success('Feedback sent successfully!');
      })
      .addCase(submitFeedback.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
      })
      .addCase(resetState, () => initialState);
  },
});

export default bookingSlice.reducer;
